import {
  forwardRef,
  ForwardRefRenderFunction,
  Ref,
  useState,
  useEffect,
  useImperativeHandle,
  useRef,
  useContext
} from 'react'
import map from 'lodash/map'
import get from 'lodash/get'

import { ReactComponent as IconMicrophone } from 'shared/assets/faMicrophone.svg'
import { ReactComponent as IconMicrophoneSlash } from 'shared/assets/faMicrophoneSlash.svg'
import { ReactComponent as IconCamera } from 'shared/assets/faCamera.svg'
import { ReactComponent as IconCameraSlash } from 'shared/assets/faCameraSlash.svg'
import { ReactComponent as IconClosedCaptioning } from 'shared/assets/faClosedCaptioning.svg'
import { ReactComponent as IconXMark } from 'shared/assets/faXMark.svg'
import { ReactComponent as FFLogo } from 'shared/assets/facesign_logo_gray.svg'
import CircleButton from 'shared/components/CircleButton'
import Toggle from 'shared/components/Toggle'
import MicDevicePicker from 'shared/components/MicDevicePicker'
import Tooltip from 'shared/components/Tooltip'
import Accordion from 'shared/components/Accordion'
import StreamContext from 'shared/contexts/StreamContext'

export interface IControlPanel {
  hide: () => void
  click: () => void
  setSpeech: (v: string) => void
}

export type Step = {
  id: string
  text: string
  isAvatar: boolean
}

type Props = {
  permissionsError: string | null
  handleClose: () => void
  permissionsChecked: boolean
  steps: Step[]
  userName: string | null
  isRecording: boolean
  orientation: 'portrait' | 'landscape' | ''
  availableLanguages: { createdAt?: number; id: string; title: string }[]
  lang: string
  onChangeLang: (lang: string) => void
  subtitlesOpenedByDefault?: boolean
}

const ControlPanel: ForwardRefRenderFunction<IControlPanel, Props> = (
  {
    permissionsError,
    handleClose,
    permissionsChecked,
    steps,
    userName,
    isRecording,
    orientation,
    availableLanguages,
    lang,
    onChangeLang,
    subtitlesOpenedByDefault = false
  },
  ref: Ref<unknown>
) => {
  const {
    controls,
    changeControls,
    selectedDeviceId,
    devices,
    changeDeviceId,
    micHealth
  } = useContext(StreamContext)
  const micProblem = controls.mic && micHealth.status !== 'ok'
  // Only one bottom panel is open at a time.
  type PanelName = 'subtitles' | 'languages' | 'mic' | 'camera'
  const [activePanel, setActivePanel] = useState<PanelName | null>(
    subtitlesOpenedByDefault ? 'subtitles' : null
  )
  const [hidding, setHidding] = useState<boolean>(false)
  const [speech, setSpeech] = useState<string>('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const togglePanel = (panel: PanelName) =>
    setActivePanel((prev) => (prev === panel ? null : panel))
  const setPanel = (panel: PanelName, show: boolean) =>
    setActivePanel(show ? panel : null)

  useImperativeHandle(ref, () => ({
    click: () => {
      setHidding(!hidding)
    },
    setSpeech: (v: string) => setSpeech(v)
  }))

  useEffect(() => {
    if (isRecording) {
      setSpeech('')
    }
  }, [isRecording])

  useEffect(() => {
    if (permissionsError) {
      changeControls({ camera: false, mic: false })
    }
  }, [permissionsError])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [steps])

  // Collapse an open panel when clicking outside the control panel.
  useEffect(() => {
    if (activePanel === null) return
    const onPointerDown = (e: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setActivePanel(null)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [activePanel])

  useEffect(() => {
    if (orientation === 'portrait' && !subtitlesOpenedByDefault) {
      setTimeout(() => {
        setHidding(true)
      }, 2000)
    }
  }, [])

  const renderStep = (s: Step) => {
    const speaker = s.isAvatar ? 'Facesign' : userName ?? 'Anonymous'
    return (
      <p key={s.id} className='text-gray-100 text-sm font-light italic'>
        {speaker}: {get(s, 'text', '')}
      </p>
    )
  }

  const onLangClick = (l: string) => {
    if (l !== lang) {
      onChangeLang(l)
    }
  }

  return (
    <div
      ref={containerRef}
      className={`transition-opacity duration-200 landscape:w-min landscape:mx-auto portrait:w-full absolute bottom-0 z-40 ${
        hidding ? 'opacity-0' : 'opacity-100'
      } left-0 right-0`}
    >
      <div className={hidding ? 'hidden' : ''}>
        <div className='relative landscape:w-full landscape:rounded-t portrait:w-full flex flex-shrink-0 flex-row gap-[20px] justify-between py-4 bg-blackAlpha-800 px-6'>
          <div className='flex flex-shrink-0 flex-grow-1 flex-row gap-[20px] absolute landscape:w-[116px] portrait:w-[calc(20%+3rem)]'>
            {!permissionsChecked && !hidding && (
              <Tooltip
                value='Enable Camera + Microphone'
                classNames='w-[calc(100%+2rem)] -left-4 -top-14'
              />
            )}
          </div>
          <div className='relative'>
            <CircleButton
              colorScheme={activePanel === 'mic' ? 'defaultActive' : 'default'}
              onClick={() => togglePanel('mic')}
              disabled={!!permissionsError}
            >
              {controls.mic ? (
                <IconMicrophone
                  fill={activePanel === 'mic' ? 'black' : 'white'}
                  fontSize='1rem'
                  width='100%'
                  height='100%'
                />
              ) : (
                <IconMicrophoneSlash
                  fill={activePanel === 'mic' ? 'black' : 'white'}
                  fontSize='1rem'
                  width='100%'
                  height='100%'
                />
              )}
            </CircleButton>
            {micProblem && (
              <span className='absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-blackAlpha-800 animate-pulse' />
            )}
          </div>
          <CircleButton
            colorScheme={activePanel === 'camera' ? 'defaultActive' : 'default'}
            onClick={() => togglePanel('camera')}
            disabled={!!permissionsError}
          >
            {controls.camera ? (
              <IconCamera
                fill={activePanel === 'camera' ? 'black' : 'white'}
                fontSize='1rem'
                width='100%'
                height='100%'
              />
            ) : (
              <IconCameraSlash
                fill={activePanel === 'camera' ? 'black' : 'white'}
                fontSize='1rem'
                width='100%'
                height='100%'
              />
            )}
          </CircleButton>
          <CircleButton
            colorScheme={
              activePanel === 'subtitles' ? 'defaultActive' : 'default'
            }
            onClick={() => togglePanel('subtitles')}
          >
            <IconClosedCaptioning
              fill={activePanel === 'subtitles' ? 'black' : 'white'}
              fontSize='1rem'
              width='100%'
              height='100%'
            />
          </CircleButton>
          {availableLanguages.length > 1 && (
            <CircleButton
              colorScheme={
                activePanel === 'languages' ? 'defaultActive' : 'default'
              }
              onClick={() => togglePanel('languages')}
            >
              <p
                className={`text-xs ${
                  activePanel === 'languages' ? 'text-black' : 'text-white'
                }`}
              >
                {lang.toUpperCase()}
              </p>
            </CircleButton>
          )}
          <CircleButton colorScheme='red' onClick={handleClose}>
            <IconXMark
              color='white'
              fontSize='1rem'
              width='100%'
              height='100%'
            />
          </CircleButton>
        </div>
        <Accordion
          show={activePanel === 'subtitles'}
          setShow={(v) => setPanel('subtitles', v)}
        >
          <div className='max-h-32 overflow-auto'>
            {map(steps, renderStep)}
            <p
              key={'speech'}
              className='text-gray-100 text-sm font-light italic'
            >
              {userName || 'Anonymous'}: {speech}
            </p>
            <div ref={messagesEndRef} />
          </div>
        </Accordion>
        <Accordion
          show={activePanel === 'languages'}
          setShow={(v) => setPanel('languages', v)}
        >
          {map(availableLanguages, (l) => (
            <div
              className='flex items-center mb-4'
              key={l.id}
              onClick={() => onLangClick(l.id)}
            >
              <input
                id={`language_${l.id}`}
                checked={lang === l.id}
                type='radio'
                value={lang}
                name={lang}
                className='w-4 h-4 text-teal-400 focus:ring-0 accent-[#38B2AC]'
                onChange={() => onLangClick(l.id)}
              />
              <label
                htmlFor={`language_${l.id}`}
                className='ms-2 text-sm font-medium text-white'
              >
                {l.title}
              </label>
            </div>
          ))}
        </Accordion>
        <Accordion
          show={activePanel === 'mic'}
          setShow={(v) => setPanel('mic', v)}
        >
          <MicDevicePicker showToggle disabled={!!permissionsError} />
        </Accordion>
        <Accordion
          show={activePanel === 'camera'}
          setShow={(v) => setPanel('camera', v)}
        >
          <Toggle
            label='Camera'
            on={controls.camera}
            disabled={!!permissionsError}
            onChange={(next) => changeControls({ ...controls, camera: next })}
          />
          <div className='mt-2 border-t border-whiteAlpha-300 pt-2'>
            {devices.length === 0 && (
              <p className='text-sm text-whiteAlpha-600'>No cameras found</p>
            )}
            {map(devices, (d) => (
              <div
                className='flex items-center mb-3'
                key={d.deviceId}
                onClick={() => changeDeviceId(d.deviceId)}
              >
                <input
                  id={`camera_${d.deviceId}`}
                  type='radio'
                  value={d.deviceId}
                  name='camera'
                  className='w-4 h-4 text-teal-400 focus:ring-0 accent-[#38B2AC]'
                  checked={selectedDeviceId === d.deviceId}
                  onChange={() => changeDeviceId(d.deviceId)}
                />
                <label
                  htmlFor={`camera_${d.deviceId}`}
                  className='ms-2 text-sm font-medium text-white'
                >
                  {d.label || 'Camera'}
                </label>
              </div>
            ))}
          </div>
        </Accordion>
        <div className='w-full mx-auto bg-blackAlpha-800 flex items-center justify-end p-1 pr-2'>
          <span className='pr-1 text-[6px] text-whiteAlpha-600'>
            Powered by
          </span>
          <FFLogo height={10} width={40} />
        </div>
      </div>
    </div>
  )
}

export default forwardRef(ControlPanel)
